/**
 * AuditChain — Zero-Knowledge Pedersen Commitment Audit Chain
 *
 * Every message generates a Pedersen commitment: C = m·G + r·H
 * where m = SHA-256(content), r = random blinding factor, G = Ristretto base, H = derived generator.
 *
 * Properties:
 * - Perfectly hiding: commitment reveals nothing about the message
 * - Computationally binding: cannot open to a different message
 * - Selective disclosure: reveal r to prove a specific message to an auditor
 * - Chain integrity: each entry links to the previous via chain_hash
 *
 * Uses Ristretto255 (prime-order group on Curve25519) via @noble/curves.
 */

// @ts-ignore — @noble/curves v2 requires .js extension in import specifiers
import { ristretto255, ristretto255_hasher } from '@noble/curves/ed25519.js'
// @ts-ignore — @noble/hashes v2 requires .js extension in import specifiers
import { sha256 } from '@noble/hashes/sha2.js'
// @ts-ignore
import { bytesToHex, hexToBytes, randomBytes } from '@noble/hashes/utils.js'

// ── Types ──────────────────────────────────────────────────────────

export interface ZKAuditEntry {
  seq: number
  timestamp: number
  node_id: string
  commitment: string           // Hex Ristretto point: C = m·G + r·H
  role: 'user' | 'assistant' | 'system'
  prev_chain: string           // Previous chain_hash
  chain_hash: string           // SHA-256(seq || commitment || prev_chain || timestamp)
  model_id?: string
  tokens_generated?: number
  inference_ms?: number
}

export interface ZKSecret {
  seq: number
  content: string
  blinding_factor: string      // Hex scalar r
  content_hash: string         // SHA-256(content) as hex
}

// ── Ristretto Pedersen Setup ───────────────────────────────────────

// G = Ristretto255 base point (standard generator)
const G = ristretto255.Point.BASE

// H = hash-to-curve of a nothing-up-my-sleeve constant.
// This ensures H is provably independent from G — no known discrete log relation.
const H_SEED = new TextEncoder().encode('TitanU-Sovereign-Pedersen-Generator-H-v1')
const H = ristretto255_hasher.hashToCurve(H_SEED)

// L = Ristretto255 scalar field order
const L = BigInt('7237005577332262213973186563042994240857116359379907606001950938285454250989')

// ── Helpers ────────────────────────────────────────────────────────

function bytesToBigInt(bytes: Uint8Array): bigint {
  let result = 0n
  for (let i = 0; i < bytes.length; i++) {
    result = (result << 8n) | BigInt(bytes[i])
  }
  return result
}

function bigIntToBytes32(n: bigint): Uint8Array {
  const bytes = new Uint8Array(32)
  let val = n
  for (let i = 31; i >= 0; i--) {
    bytes[i] = Number(val & 0xffn)
    val >>= 8n
  }
  return bytes
}

function scalarFromBytes(hash: Uint8Array): bigint {
  return bytesToBigInt(hash) % L
}

/**
 * Compute Pedersen commitment: C = m·G + r·H
 */
function pedersenCommit(messageScalar: bigint, blindingScalar: bigint): typeof G {
  const mG = G.multiply(messageScalar)
  const rH = H.multiply(blindingScalar)
  return mG.add(rH)
}

/**
 * Generate a random blinding factor in [1, L-1]
 */
function randomBlindingFactor(): bigint {
  const bytes = randomBytes(64)
  return (bytesToBigInt(bytes) % (L - 1n)) + 1n
}

/**
 * SHA-256 hash as hex string
 */
function sha256Hex(data: string): string {
  return bytesToHex(sha256(new TextEncoder().encode(data)))
}

// ── IndexedDB Persistence ──────────────────────────────────────────

const DB_NAME = 'titan-audit-chain'
const DB_VERSION = 1
const STORE_ENTRIES = 'entries'
const STORE_SECRETS = 'secrets'
const STORE_META = 'meta'

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_ENTRIES)) {
        db.createObjectStore(STORE_ENTRIES, { keyPath: 'seq' })
      }
      if (!db.objectStoreNames.contains(STORE_SECRETS)) {
        db.createObjectStore(STORE_SECRETS, { keyPath: 'seq' })
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'key' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function putRecord<T>(storeName: string, record: T): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite')
    tx.objectStore(storeName).put(record)
    tx.oncomplete = () => { db.close(); resolve() }
    tx.onerror = () => { db.close(); reject(tx.error) }
  })
}

async function getRecord<T>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly')
    const req = tx.objectStore(storeName).get(key)
    req.onsuccess = () => { db.close(); resolve(req.result) }
    req.onerror = () => { db.close(); reject(req.error) }
  })
}

async function getAllRecords<T>(storeName: string): Promise<T[]> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly')
    const req = tx.objectStore(storeName).getAll()
    req.onsuccess = () => { db.close(); resolve(req.result) }
    req.onerror = () => { db.close(); reject(req.error) }
  })
}

// ── Node Identity ──────────────────────────────────────────────────

let _nodeId: string | null = null

async function getNodeId(): Promise<string> {
  if (_nodeId) return _nodeId

  const stored = await getRecord<{ key: string; value: string }>(STORE_META, 'node_id')
  if (stored) {
    _nodeId = stored.value
    return _nodeId
  }

  // Generate new node identity from random seed hash
  const seed = randomBytes(32)
  const hash = sha256(seed)
  const id = bytesToHex(hash).slice(0, 16)
  _nodeId = id
  await putRecord(STORE_META, { key: 'node_id', value: id })
  return id
}

// ── AuditChain Class ───────────────────────────────────────────────

class AuditChainEngine {
  private seq = 0
  private prevChain = '0000000000000000000000000000000000000000000000000000000000000000'
  private initialized = false

  /**
   * Get (or generate) the stable 16-char hex node identity.
   * Public accessor for the module-level getNodeId helper.
   */
  async getNodeId(): Promise<string> {
    return getNodeId()
  }

  /**
   * Initialize the chain — loads state from IndexedDB
   */
  async init(): Promise<void> {
    if (this.initialized) return

    try {
      const entries = await getAllRecords<ZKAuditEntry>(STORE_ENTRIES)
      if (entries.length > 0) {
        const latest = entries.reduce((a, b) => a.seq > b.seq ? a : b)
        this.seq = latest.seq
        this.prevChain = latest.chain_hash
      }
      this.initialized = true
      console.log(`[AuditChain] Initialized — seq=${this.seq}, chain=${this.prevChain.slice(0, 12)}...`)
    } catch {
      // IndexedDB not available (private browsing, etc.) — run in-memory
      this.initialized = true
      console.warn('[AuditChain] IndexedDB unavailable — running in-memory only')
    }
  }

  /**
   * Append a new message to the audit chain.
   * Returns the ZK commitment and chain hash.
   */
  async appendEntry(
    role: 'user' | 'assistant' | 'system',
    content: string,
    metadata?: { model_id?: string; tokens_generated?: number; inference_ms?: number }
  ): Promise<ZKAuditEntry> {
    await this.init()

    const nodeId = await getNodeId()
    const timestamp = Date.now()
    this.seq++

    // 1. Hash content to scalar
    const contentHash = sha256(new TextEncoder().encode(content))
    const m = scalarFromBytes(contentHash)

    // 2. Generate random blinding factor
    const r = randomBlindingFactor()

    // 3. Compute Pedersen commitment: C = m·G + r·H
    const commitment = pedersenCommit(m, r)
    const commitmentHex = bytesToHex(commitment.toBytes())

    // 4. Compute chain hash: SHA-256(seq || commitment || prev_chain || timestamp)
    const chainInput = `${this.seq}|${commitmentHex}|${this.prevChain}|${timestamp}`
    const chainHash = sha256Hex(chainInput)

    // 5. Build entry (public — safe to broadcast)
    const entry: ZKAuditEntry = {
      seq: this.seq,
      timestamp,
      node_id: nodeId,
      commitment: commitmentHex,
      role,
      prev_chain: this.prevChain,
      chain_hash: chainHash,
      ...metadata
    }

    // 6. Build secret (private — NEVER leaves the node)
    const secret: ZKSecret = {
      seq: this.seq,
      content,
      blinding_factor: bytesToHex(bigIntToBytes32(r)),
      content_hash: bytesToHex(contentHash)
    }

    // 7. Persist to IndexedDB
    try {
      await putRecord(STORE_ENTRIES, entry)
      await putRecord(STORE_SECRETS, secret)
    } catch {
      // IndexedDB failed — chain still valid in memory
    }

    // 8. Advance chain
    this.prevChain = chainHash

    console.log(`[AuditChain] #${this.seq} ${role} | commitment=${commitmentHex.slice(0, 16)}... | chain=${chainHash.slice(0, 12)}...`)

    return entry
  }

  /**
   * Get the latest chain hash (root of the chain)
   */
  getChainRoot(): string {
    return this.prevChain
  }

  /**
   * Get the current sequence number
   */
  getSeq(): number {
    return this.seq
  }

  /**
   * Verify a specific entry by recomputing the commitment from content + blinding factor.
   * This is the "selective disclosure" operation.
   */
  async verifyEntry(seq: number): Promise<{ valid: boolean; entry?: ZKAuditEntry }> {
    const entry = await getRecord<ZKAuditEntry>(STORE_ENTRIES, seq)
    const secret = await getRecord<ZKSecret>(STORE_SECRETS, seq)

    if (!entry || !secret) return { valid: false }

    // Recompute commitment from secret
    const m = scalarFromBytes(hexToBytes(secret.content_hash))
    const r = bytesToBigInt(hexToBytes(secret.blinding_factor))
    const recomputed = pedersenCommit(m, r)
    const recomputedHex = bytesToHex(recomputed.toBytes())

    return {
      valid: recomputedHex === entry.commitment,
      entry
    }
  }

  /**
   * Verify the full chain integrity — check that all chain_hash links are valid.
   */
  async verifyChain(): Promise<{ valid: boolean; length: number; brokenAt?: number }> {
    const entries = await getAllRecords<ZKAuditEntry>(STORE_ENTRIES)
    entries.sort((a, b) => a.seq - b.seq)

    let prevChain = '0000000000000000000000000000000000000000000000000000000000000000'

    for (const entry of entries) {
      const chainInput = `${entry.seq}|${entry.commitment}|${prevChain}|${entry.timestamp}`
      const expectedHash = sha256Hex(chainInput)

      if (expectedHash !== entry.chain_hash) {
        return { valid: false, length: entries.length, brokenAt: entry.seq }
      }
      if (entry.prev_chain !== prevChain) {
        return { valid: false, length: entries.length, brokenAt: entry.seq }
      }

      prevChain = entry.chain_hash
    }

    return { valid: true, length: entries.length }
  }

  /**
   * Export the public chain (no secrets — safe to send to mesh/auditors)
   */
  async exportPublicChain(): Promise<ZKAuditEntry[]> {
    const entries = await getAllRecords<ZKAuditEntry>(STORE_ENTRIES)
    return entries.sort((a, b) => a.seq - b.seq)
  }

  /**
   * Export a disclosure package for a specific entry (for auditor verification)
   */
  async exportDisclosure(seq: number): Promise<{
    entry: ZKAuditEntry
    content_hash: string
    blinding_factor: string
  } | null> {
    const entry = await getRecord<ZKAuditEntry>(STORE_ENTRIES, seq)
    const secret = await getRecord<ZKSecret>(STORE_SECRETS, seq)
    if (!entry || !secret) return null

    return {
      entry,
      content_hash: secret.content_hash,
      blinding_factor: secret.blinding_factor
    }
  }
}

// ── Singleton Export ────────────────────────────────────────────────

export const auditChain = new AuditChainEngine()

export default auditChain
